import React, { useState } from "react";
import { Search, Download, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/upr-api";

export default function MyOrders() {
  const [email, setEmail] = useState("");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [orders, setOrders] = useState([]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setSubmitted(false);
    try {
      const { data } = await api.post("/downloads/lookup", {
        email: email.trim(),
        reference: reference.trim(),
      });
      setOrders(data.orders || []);
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-20 max-w-3xl mx-auto" data-testid="my-orders-page">
      <div className="upr-eyebrow mb-6"><span className="upr-divider"/>Self-service</div>
      <h1 className="font-heading font-black tracking-tighter leading-[0.88] text-5xl md:text-7xl" style={{ letterSpacing: "-0.035em" }}>
        Find my<br/>downloads.
      </h1>
      <p className="mt-5 max-w-xl text-base md:text-lg" style={{ color: "var(--upr-muted)" }}>
        Lost your confirmation email? Enter the email used at checkout and the last few characters of your order reference (you&apos;ll find it as <strong>#xxxxxx</strong> in your receipt).
      </p>

      <form onSubmit={onSubmit} className="mt-10 grid gap-3 max-w-lg" data-testid="lookup-form">
        <label className="block">
          <span className="upr-eyebrow block mb-2">Email used at checkout</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="lookup-email"
            placeholder="you@example.com"
            className="w-full p-3 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
            style={{ borderColor: "var(--upr-border)" }}
          />
        </label>
        <label className="block">
          <span className="upr-eyebrow block mb-2">Order reference (last 6 chars)</span>
          <input
            type="text"
            required
            minLength={4}
            maxLength={20}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            data-testid="lookup-reference"
            placeholder="abc123"
            className="w-full p-3 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)] font-mono tracking-widest uppercase"
            style={{ borderColor: "var(--upr-border)" }}
          />
        </label>
        <button
          type="submit"
          disabled={busy}
          className="upr-btn self-start"
          data-testid="lookup-submit"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {busy ? "Searching…" : "Find my orders"}
        </button>
      </form>

      {submitted && (
        <div className="mt-12" data-testid="lookup-results">
          {orders.length === 0 ? (
            <div className="p-6 text-center" style={{ background: "var(--upr-surface)", border: "1px solid var(--upr-border)" }}>
              <div className="font-heading font-extrabold text-lg">No orders found.</div>
              <p className="mt-2 text-sm" style={{ color: "var(--upr-muted)" }}>
                Double-check your email spelling and the order reference. Still stuck? <a href="/contact" className="upr-link-underline">Drop us a line</a>.
              </p>
            </div>
          ) : (
            <>
              <div className="upr-eyebrow mb-4" style={{ color: "var(--upr-secondary)" }}>
                <ShieldCheck size={11} className="inline-block mr-1" />
                Found {orders.length} order{orders.length === 1 ? "" : "s"}
              </div>
              <div className="space-y-6">
                {orders.map((o, i) => (
                  <article
                    key={o.reference + i}
                    className="border p-5"
                    style={{ borderColor: "var(--upr-border)" }}
                    data-testid={`lookup-order-${i}`}
                  >
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div>
                        <div className="upr-eyebrow" style={{ fontSize: "0.62rem" }}>Order {o.reference}</div>
                        <div className="font-heading font-extrabold text-xl mt-1">
                          {o.items.map((it, j) => (
                            <span key={j}>
                              {j > 0 && <span style={{ color: "var(--upr-muted)" }}> · </span>}
                              {it.merch_name}{it.variant_label ? ` (${it.variant_label})` : ""}
                              <span className="upr-eyebrow ml-1" style={{ fontSize: "0.62rem", color: "var(--upr-muted)" }}>× {it.quantity}</span>
                            </span>
                          ))}
                        </div>
                        <div className="text-xs mt-1" style={{ color: "var(--upr-muted)" }}>
                          {new Date(o.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="font-heading font-extrabold">{(o.currency || "EUR").toUpperCase()} {Number(o.amount || 0).toFixed(2)}</div>
                    </div>

                    {o.downloads && o.downloads.length > 0 ? (
                      <div className="mt-5 space-y-4">
                        {o.downloads.map((d, k) => (
                          <div key={d.token} data-testid={`lookup-download-${i}-${k}`}>
                            <div className="upr-eyebrow mb-2" style={{ color: "var(--upr-primary)", fontSize: "0.6rem" }}>
                              Included MP3 download
                            </div>
                            <div className="font-heading font-extrabold">{d.release.title}</div>
                            <div className="text-xs" style={{ color: "var(--upr-muted)" }}>
                              {d.release.cat_num} · Link valid until {new Date(d.expires_at).toLocaleDateString()}
                            </div>
                            <ul className="mt-3 space-y-1.5">
                              {d.tracks.map((t, x) => (
                                <li key={x} className="flex items-center justify-between gap-3 p-2 border" style={{ borderColor: "var(--upr-border)" }}>
                                  <span className="text-sm truncate">{x + 1}. {t.title || `Track ${x + 1}`}</span>
                                  <a
                                    href={t.download_url}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="upr-btn"
                                    data-testid={`lookup-download-track-${i}-${k}-${x}`}
                                  >
                                    <Download size={12} /> Download
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-4 text-xs" style={{ color: "var(--upr-muted)" }}>
                        This order has no digital downloads attached.
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
